import { Carousel } from "@coherence-design-system/controls";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { getFeatureDataSelector } from "../../../../slices/ExploreProducts.slice";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";
import { getLineChartData, groupBy } from "../../../../Utils/js-common";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";
import { CarouselLineChartDataProps, FeaturesByNameProps, ILineChartData } from "../../Common/ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";

const DisplayLineChart = ({ data }: { data: ILineChartData[] }) => {
  const lineMetricsContainerRef = useRef<HTMLDivElement>(null);
  const resizedWidth = useAppSelector(selectViewport).offsetWidth;
  const [width, setWidth] = useState(445);
  const [, setOffsetWidth] = useState(resizedWidth);
  const filters = useContext(ProductViewFiltersContext);

  const concatMetricsForMinMax = useMemo(
    () =>
      data
        .map(({ data }) => [...data.map(({ y }) => y)])
        .reduce((currentValueYAxis, premiumYAxis) => currentValueYAxis.concat(premiumYAxis)),
    [data]
  );

  useEffect(() => {
    setOffsetWidth(resizedWidth);
  }, [resizedWidth]);

  useLayoutEffect(() => {
    if (lineMetricsContainerRef.current) {
      setWidth(lineMetricsContainerRef.current?.offsetWidth);
    }
  });

  const customProps = {
    productName: filters?.productName,
    productFilters: JSON.stringify(filters)
  };

  useEffect(() => {
    logEvent("Detailed View Tab - Features Telemetry", "Product View Page", "DetailedViewTab", customProps);
  }, [filters]);

  return (
    <div ref={lineMetricsContainerRef}>
      <LineChartComponent
        data={getLineChartData(data)}
        height={400}
        width={width}
        allowResize={true}
        yMinValue={Math.min(...concatMetricsForMinMax)}
        yMaxValue={Math.max(...concatMetricsForMinMax)}
      />
    </div>
  );
};

const getCarouselItem = (feature: CarouselLineChartDataProps) => (
  <CustomCard cardHeaderTitle={feature.name} styles={PRODUCT_VIEW_STYLES.TAB.DETAILED_VIEW.CSS_STYLES.customCardStyles}>
    <DisplayLineChart data={feature.data} />
  </CustomCard>
);

const formatFeaturesCallback = (
  data: FeaturesByNameProps[],
  isPurviewProduct?: boolean
): CarouselLineChartDataProps => ({
  name: data.at(0)?.featureName || "",
  data: isPurviewProduct
    ? [
        {
          name: "MPU",
          data: data.map(({ currentValue, snapshotDate }) => ({ x: snapshotDate, y: currentValue }))
        },
        {
          name: "MDPU",
          data: data.map(({ currentMDPUValue, snapshotDate }) => ({ x: snapshotDate, y: currentMDPUValue }))
        }
      ]
    : [
        {
          name: "Current Value",
          data: data.map(({ currentValue, snapshotDate }) => ({ x: snapshotDate, y: currentValue }))
        }
      ]
});

export const DetailedViewTab = () => {
  const { data, error, isLoading, isSuccess, isError } = useAppSelector(getFeatureDataSelector);
  const formatFeaturesDataPredicate = useCallback(formatFeaturesCallback, []);
  const displayAPIsException = isError && <DisplayAPIsException error={error} height={345} />;
  const [searchParams] = useSearchParams();
  const productName = searchParams.get("productname");
  const isPurviewProduct = productName?.toLowerCase().includes("purview");
  const isSecurityCopilotProduct = productName?.toLowerCase().includes("security copilot");
  const items = useMemo(() => {
    return Object.values<FeaturesByNameProps[]>(groupBy(data?.featuresByName || [], "featureId"))
      .map((group) => formatFeaturesDataPredicate(group, isPurviewProduct))
      .map((result) => getCarouselItem(result));
  }, [data, isPurviewProduct]);
  const displayShimmer = isLoading && (
    <CustomShimmer columnCount={3} styles={PRODUCT_VIEW_STYLES.TAB.DETAILED_VIEW.CSS_STYLES.customShimmerStyles} />
  );
  const displayCarousel = isSuccess && data.featuresByName.length > 0 && (
    <Carousel {...PRODUCT_VIEW_DATA.TABS.TAB.FEATURE_VIEW.DETAILED_VIEW.INITIAL_CAROUSEL_DATA} items={items} />
  );

  const displayNoDataMessage = isSuccess && <div>{PRODUCT_VIEW_DATA.PRODUCT_VIEW.VIEW_NO_DATA_MESSAGE}</div>;
  return (
    displayAPIsException || (
      <div className={PRODUCT_VIEW_STYLES.TAB.DETAILED_VIEW.CLASS_NAMES.carouselContainer}>
        {displayShimmer}
        {isSecurityCopilotProduct && !isLoading ? (
          <div
            style={{
              alignItems: "center",
              display: "flex",
              fontSize: 18,
              fontWeight: 600,
              justifyContent: "center",
              padding: 200
            }}
          >
            No data available to show for Detailed view for {productName} product
          </div>
        ) : (
          displayCarousel || displayNoDataMessage
        )}
      </div>
    )
  );
};
